import { z } from "zod";

/**
 * Shared validation schemas for authentication and user management
 * Follows DRY principle and ensures consistent validation across the app
 *
 * Following Kaizen principles:
 * - Type-safe validation with zod
 * - Centralized error messages
 * - Reusable across features
 */

// Common password validation rules
export const COMMON_PASSWORDS = [
  "12345678",
  "password",
  "password123",
  "qwerty",
  "abc123",
  "abcd1234",
  "letmein",
  "iloveyou",
  "admin123",
] as const;

/**
 * Base password schema with common validation rules
 * Use this as a building block for password fields
 */
export const passwordSchema = z
  .string()
  .min(8, "Password must be at least 8 characters long")
  .regex(/[a-z]/, "Password must contain at least one lowercase letter")
  .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
  .regex(/\d/, "Password must contain at least one number")
  .regex(
    /[!@#$%^&*(),.?":{}|<>_]/,
    "Password must contain at least one special character",
  )
  .refine(
    (val) => !COMMON_PASSWORDS.includes(val.toLowerCase() as any),
    "Password is too common, please choose another one",
  );

/**
 * Email schema with standard validation
 */
export const emailSchema = z
  .string()
  .min(1, "Email is required")
  .email("Invalid email address");

/**
 * Name validation schemas
 */
export const firstNameSchema = z
  .string()
  .min(1, "First name is required")
  .max(50, "First name must be less than 50 characters");

export const lastNameSchema = z
  .string()
  .min(1, "Last name is required")
  .max(50, "Last name must be less than 50 characters");

export const usernameSchema = z
  .string()
  .min(1, "Username is required")
  .min(3, "Username must be at least 3 characters")
  .max(30, "Username must be less than 30 characters")
  .regex(
    /^[a-zA-Z0-9_-]+$/,
    "Username can only contain letters, numbers, underscores, and hyphens",
  );

/**
 * Registration schema for new users
 */
export const registerSchema = z
  .object({
    first_name: firstNameSchema,
    last_name: lastNameSchema,
    username: usernameSchema,
    email: emailSchema,
    password: passwordSchema,
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

export type RegisterSchema = z.infer<typeof registerSchema>;

/**
 * Simplified registration schema (for manager adding users)
 * No username or confirmation required
 */
export const addUserSchema = z.object({
  first_name: firstNameSchema,
  last_name: lastNameSchema,
  email: emailSchema,
  password: passwordSchema,
});

export type AddUserSchema = z.infer<typeof addUserSchema>;

/**
 * Login schema
 */
export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, "Password is required"),
});

export type LoginSchema = z.infer<typeof loginSchema>;

/**
 * Change password schema
 */
export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, "Current password is required"),
    newPassword: passwordSchema,
    confirmPassword: z.string().min(1, "Please confirm your new password"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  })
  .refine((data) => data.currentPassword !== data.newPassword, {
    message: "New password must be different from current password",
    path: ["newPassword"],
  });

export type ChangePasswordSchema = z.infer<typeof changePasswordSchema>;

/**
 * Profile update schema
 */
export const profileUpdateSchema = z.object({
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  email: emailSchema,
});

export type ProfileUpdateSchema = z.infer<typeof profileUpdateSchema>;

/**
 * Forgot password schema (email only)
 */
export const forgotPasswordEmailSchema = z.object({
  email: emailSchema,
});

export type ForgotPasswordEmailSchema = z.infer<
  typeof forgotPasswordEmailSchema
>;

/**
 * Reset password schema (with token)
 */
export const resetPasswordSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

export type ResetPasswordSchema = z.infer<typeof resetPasswordSchema>;
